import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "@/components/ui/hover-card";
import { Slot } from "@radix-ui/react-slot";
import { type VariantProps, cva } from "class-variance-authority";
import { Loader2 } from "lucide-react";
import * as React from "react";
import { useState } from "react";

import { cn } from "@/lib/utils";

const buttonVariants = cva(
  "inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground hover:bg-primary/90",
        destructive:
          "bg-destructive text-destructive-foreground hover:bg-destructive/90",
        outline:
          "border border-input bg-background hover:bg-accent hover:text-accent-foreground",
        secondary:
          "bg-secondary text-secondary-foreground hover:bg-secondary/80",
        ghost: "hover:bg-accent hover:text-accent-foreground",
        link: "text-primary underline-offset-4 hover:underline",
      },
      size: {
        xs: "h-8 rounded-md px-2",
        default: "h-10 px-4 py-2",
        sm: "h-9 rounded-md px-3",
        lg: "h-11 rounded-md px-8",
        icon: "h-10 w-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

export interface ButtonProps
  extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, "disabled">,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  readOnly?: boolean | string | React.ReactNode;
  disabled?: boolean | string | React.ReactNode;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant,
      size,
      asChild = false,
      disabled = false,
      readOnly = false,
      ...props
    },
    ref,
  ) => {
    const Comp = asChild ? Slot : "button";

    const buttonElement = (
      <Comp
        className={cn(
          buttonVariants({ variant, size, className }),
          Boolean(readOnly) && "cursor-not-allowed disabled:opacity-100",
        )}
        ref={ref}
        disabled={Boolean(disabled) || Boolean(readOnly)}
        {...props}
      />
    );

    const message = React.useMemo(() => {
      if (disabled) {
        return disabled;
      }
      if (readOnly) {
        return readOnly;
      }
      return "";
    }, [disabled, readOnly]);

    if (
      typeof disabled === "string" ||
      typeof readOnly === "string" ||
      typeof readOnly === "object" ||
      typeof disabled === "object"
    ) {
      return (
        <HoverCard openDelay={1000} closeDelay={0}>
          <HoverCardTrigger asChild>{buttonElement}</HoverCardTrigger>
          {Boolean(disabled || readOnly) && (
            <HoverCardContent
              align="start"
              sideOffset={-5}
              className="w-fit max-w-[500px] p-3"
            >
              {typeof message === "string" ? (
                <p className="text-sm">{message}</p>
              ) : (
                message
              )}
            </HoverCardContent>
          )}
        </HoverCard>
      );
    }

    return buttonElement;
  },
);

Button.displayName = "Button";

export interface AsyncButtonProps extends Omit<ButtonProps, "onClick"> {
  onClick?: (
    event: React.MouseEvent<HTMLButtonElement>,
  ) => Promise<void> | void;
}

export const AsyncButton = React.forwardRef<
  HTMLButtonElement,
  AsyncButtonProps
>(({ onClick, children, disabled, ...props }, ref) => {
  const [loading, setLoading] = useState(false);

  const handleClick = async (event: React.MouseEvent<HTMLButtonElement>) => {
    if (!onClick) return;

    setLoading(true);
    try {
      await onClick(event);
    } finally {
      setLoading(false);
    }
  };

  const isIconComponent = (element: React.ReactNode): boolean => {
    if (!React.isValidElement(element)) return false;

    if (element.type === "svg") return true;

    const className = element.props?.className;
    if (typeof className === "string") {
      if (
        /\b(w-\d+\s+h-\d+|h-\d+\s+w-\d+)\b/.test(className) ||
        className.includes("lucide")
      ) {
        return true;
      }
    }

    if (typeof element.type === "function") {
      const displayName =
        (element.type as any).displayName || (element.type as any).name || "";
      if (displayName.endsWith("Icon") || displayName.includes("Lucide")) {
        return true;
      }
    }

    return false;
  };

  const processChildren = (childrenNodes: React.ReactNode): React.ReactNode => {
    if (!childrenNodes) {
      return <Loader2 className="h-4 w-4 animate-spin" />;
    }

    let hasIcon = false;
    let hasText = false;
    const processedChildren: React.ReactNode[] = [];

    React.Children.forEach(childrenNodes, (child) => {
      if (typeof child === "string" && child.trim() !== "") {
        hasText = true;
        processedChildren.push(child);
      } else if (React.isValidElement(child) && isIconComponent(child)) {
        hasIcon = true;
        if (loading) {
          const iconClassName = child.props?.className || "h-4 w-4";
          processedChildren.push(
            <Loader2
              key="spinner"
              className={cn(iconClassName, "animate-spin")}
            />,
          );
        } else {
          processedChildren.push(child);
        }
      } else {
        processedChildren.push(child);
      }
    });

    if (hasIcon) {
      return processedChildren;
    }

    if (hasText && loading) {
      return (
        <>
          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          {processedChildren}
        </>
      );
    }

    if (!hasText && !hasIcon && loading) {
      return <Loader2 className="h-4 w-4 animate-spin" />;
    }

    return processedChildren;
  };

  return (
    <Button
      ref={ref}
      onClick={handleClick}
      disabled={disabled || loading}
      {...props}
    >
      {processChildren(children)}
    </Button>
  );
});

AsyncButton.displayName = "AsyncButton";

export { Button, buttonVariants };
